import { bind } from 'decko'
import { LiveElement, LiveNodeList } from 'live-node-list'
import { templateMinicartItem } from '../templates/mini-cart-item'
import { CartItemError, InvalidDiscountCodeError } from './cart-errors'
import { formatMoney, retrieveMetaobject } from '../helpers'
import { templateCartItem } from '../templates/cart-item'
import {
  templateCustomerDiscountItem,
  templateDiscountItem,
} from '../templates/cart-discounts'
import { CartProcessingEndEvent } from './cart-events'
import { sellingPlansQuery, variantMetafieldQuery } from '../queries'
import { fetchFromApi } from './api'
import { productMetafields, variantMetafields } from './product-form'

const DYNAMIC_SECTIONS = ['product-upsells']

export default class MiniCart {
  /**
   * @type {object}
   */
  cart = null

  /**
   * @type {LiveElement}
   */
  minicart = new LiveElement('.mini-cart')

  /**
   * @type {LiveElement}
   */
  minicartInner = new LiveElement('.mini-cart__inner')

  /**
   * @type {LiveElement}
   */
  minicartItemContainer = new LiveElement('.mini-cart__items')

  /**
   * @type {LiveElement}
   */
  minicartEmptyState = new LiveElement('.mini-cart__empty')

  /**
   * @type {LiveElement}
   */
  minicartFooter = new LiveElement('.mini-cart__footer')

  /**
   * @type {LiveElement}
   */
  cartTable = new LiveElement('.cart-table tbody')

  /**
   * @type {LiveNodeList}
   */
  errorContainers = new LiveNodeList('[data-cart-errors]')

  /**
   * @type {LiveNodeList}
   */
  toggles = new LiveNodeList('[data-minicart-toggle]')

  /**
   * @type {LiveNodeList}
   */
  removeButtons = new LiveNodeList('[data-remove-line-item]')

  /**
   * @type {LiveNodeList}
   */
  subtotals = new LiveNodeList('[data-cart-subtotal]')

  /**
   * @type {LiveNodeList}
   */
  totals = new LiveNodeList('[data-cart-total]')

  /**
   * @type {LiveNodeList}
   */
  counts = new LiveNodeList('[data-cart-count]')

  /**
   * @type {LiveNodeList}
   */
  deliveryProgressContainer = new LiveElement('.mini-cart__delivery-progress')
  /**
   * @type {LiveNodeList}
   */
  deliveryNotificationMessages = new LiveNodeList(
    '.delivery-notification-message',
  )

  /**
   * @type {LiveNodeList}
   */
  discountContainers = new LiveNodeList('[data-cart-discounts]')

  /**
   * @type {LiveNodeList}
   */
  discountForms = new LiveNodeList('[data-discount-form]')

  /**
   * @type {LiveNodeList}
   */
  discountRemoveButtons = new LiveNodeList('[data-discount-code-remove]')

  /**
   * @type {LiveNodeList}
   */
  sellingPlanFields = new LiveNodeList(
    '[data-selling-plan-toggle], [data-selling-plan-select]',
  )

  /**
   * @type {LiveNodeList}
   */
  minicartTotals = new LiveNodeList(
    '.mini-cart__subtotal, .mini-cart__total, .discounts-list',
  )

  constructor() {
    this.registerListeners()
  }

  registerListeners() {
    // Handlers for page load
    document.addEventListener('DOMContentLoaded', this.reload)

    // Handlers for cart update events
    document.addEventListener('cart:add', this.reload)
    document.addEventListener('cart:remove', this.reload)
    document.addEventListener('cart:update', this.reload)
    document.addEventListener('cart:discount:apply', this.reload)
    document.addEventListener('cart:discount:remove', this.reload)

    // Handlers for errors
    document.addEventListener('cart:error', event => {
      this.renderError(event.error)
    })

    // Handlers for opening/closing minicart
    if (!document.body.classList.contains('template-cart')) {
      document.addEventListener('cart:add', this.openMinicart)
      document.addEventListener('cart:error', this.openMinicart)
    }

    this.minicart.addEventListener('click', this.closeMinicart)
    this.minicartInner.addEventListener('click', event =>
      event.stopPropagation(),
    )
    this.toggles.addEventListener('click', this.handleToggleClick)

    this.removeButtons.addEventListener('click', this.handleRemoveButtonClick)
    this.sellingPlanFields.addEventListener(
      'change',
      this.handleSellingPlanChange,
    )
    this.sellingPlanFields.addEventListener(
      'input',
      this.handleSellingPlanChange,
    )

    document.addEventListener('cart:processing:start', event => {
      if ('lineItem' in event) {
        this.clearItemErrors(event.lineItem.lineId)
      }

      this.minicart.item.setAttribute('aria-busy', 'true')
      this.cartTable.item?.setAttribute('aria-busy', 'true')
    })

    document.addEventListener('cart:processing:end', () => {
      this.minicart.item.setAttribute('aria-busy', 'false')
      this.cartTable.item?.setAttribute('aria-busy', 'false')
    })

    this.discountForms.addEventListener('submit', this.handleDiscountSubmit)
    this.discountRemoveButtons.addEventListener(
      'click',
      this.handleDiscountRemove,
    )
  }

  @bind
  openMinicart() {
    this.minicart.item?.setAttribute('aria-hidden', 'false')
    this.toggles.forEach(toggle => toggle.setAttribute('aria-expanded', 'true'))
  }

  @bind
  closeMinicart() {
    this.minicart.item?.setAttribute('aria-hidden', 'true')
    this.toggles.forEach(toggle =>
      toggle.setAttribute('aria-expanded', 'false'),
    )
  }

  @bind
  handleToggleClick(event) {
    event.preventDefault()
    const target = event.currentTarget || event.target
    target?.blur()

    if (this.minicart.item.getAttribute('aria-hidden') === 'true') {
      this.openMinicart()
    } else {
      this.closeMinicart()
    }
  }

  @bind
  async reload() {
    this.cart = await window.app.components.cartApi?.getCart()
    this.clearAllErrors()

    await this.populateMetafields(productMetafields, variantMetafields)
    await this.populateSellingPlans()

    await this.rerenderSection()

    await this.buildCart()
    await this.buildMinicart()
    await this.buildDiscounts()
    this.updateSubtotal()
    this.updateTotal()
    this.updateCount()
    this.updateDeliveryMessages()
  }

  @bind
  async rerenderSection() {
    const response = await fetch(`/sections?section_id=mini-cart`, {
      credentials: 'include',
    })
    const newHTML = await response.text()
    const dummy = document.createElement('div')
    dummy.innerHTML = newHTML

    for (const section of DYNAMIC_SECTIONS) {
      const newSection = dummy.querySelector(`#${section}`)
      const oldSection = this.minicart.item?.querySelector(`#${section}`)

      oldSection.replaceWith(newSection)
    }
  }

  @bind
  async populateMetafields(keys = [], variantKeys = []) {
    const ids = this.cart.items.map(item => item.product_id)
    const metafieldQuery = variantMetafieldQuery(ids, keys, variantKeys)
    const metafieldData = await fetchFromApi(metafieldQuery)

    console.log(metafieldData)

    this.cart.items = await Promise.all(
      this.cart.items.map(async item => {
        item.metafields = {}

        for (const fullKey of keys) {
          const [namespace, key] = fullKey.split('.')

          if (!item.metafields[namespace]) {
            item.metafields[namespace] = {}
          }

          let value = metafieldData.data.products?.edges?.find(
            edge => edge.node.id === `gid://shopify/Product/${item.product_id}`,
          )?.node[`${namespace}___${key}`]?.value

          if (value?.startsWith('gid://shopify/Metaobject/')) {
            value = await retrieveMetaobject(value)
          }

          item.metafields[namespace][key] = value
        }

        item.variant_metafields = {}
        for (const fullKey of variantKeys) {
          const [namespace, key] = fullKey.split('.')

          if (!item.variant_metafields[namespace]) {
            item.variant_metafields[namespace] = {}
          }

          let value = metafieldData.data.products?.edges
            ?.find(
              edge =>
                edge.node.id === `gid://shopify/Product/${item.product_id}`,
            )
            ?.node.variants?.edges?.find(
              edge =>
                edge.node.id ===
                `gid://shopify/ProductVariant/${item.variant_id}`,
            )?.node[`${namespace}___${key}`]?.value

          console.log(namespace, key, value)

          if (value?.startsWith('gid://shopify/Metaobject/')) {
            value = await retrieveMetaobject(value)
          }

          item.variant_metafields[namespace][key] = value
        }

        console.log(item)
        return item
      }),
    )
  }

  @bind
  async populateSellingPlans() {
    const ids = this.cart.items.map(item => item.product_id)
    const sellingPlanQuery = sellingPlansQuery(ids)
    const sellingPlanData = await fetchFromApi(sellingPlanQuery)

    this.cart.items = this.cart.items.map(item => {
      item.sellingPlanGroups = sellingPlanData.data.products?.edges
        .find(
          edge => edge.node.id === `gid://shopify/Product/${item.product_id}`,
        )
        ?.node.sellingPlanGroups.edges.map(edge => edge.node)
        .map(group => ({
          name: group?.name,
          sellingPlans: group.sellingPlans?.edges.map(edge => {
            edge.node.id = edge.node.id.replace(
              'gid://shopify/SellingPlan/',
              '',
            )
            return edge.node
          }),
        }))

      return item
    })
  }

  @bind
  async buildCart() {
    if (!this.cartTable.item) {
      return
    }

    if (this.cart.items.length === 0) {
      window.location = window.location
    }

    this.cartTable.item.innerHTML = ''

    let i = 0
    for (const item of this.cart.items) {
      const template = await templateCartItem(item, i + 1)
      this.cartTable.item.insertAdjacentHTML('beforeend', template)
      i++
    }
  }

  @bind
  async buildMinicart() {
    if (!this.minicartItemContainer.item) {
      return
    }

    this.minicartItemContainer.item.innerHTML = ''

    if (this.cart.items.length === 0) {
      this.minicartItemContainer.item.setAttribute('aria-hidden', 'true')
      this.minicartFooter.item.setAttribute('aria-hidden', 'true')
      this.minicartTotals.forEach(item =>
        item.setAttribute('aria-hidden', 'true'),
      )
      this.minicartEmptyState.item.setAttribute('aria-hidden', 'false')
    } else {
      this.minicartItemContainer.item.setAttribute('aria-hidden', 'false')
      this.minicartFooter.item.setAttribute('aria-hidden', 'false')
      this.minicartTotals.forEach(item =>
        item.setAttribute('aria-hidden', 'false'),
      )
      this.minicartEmptyState.item.setAttribute('aria-hidden', 'true')
    }

    let i = 0
    for (const item of this.cart.items) {
      const template = await templateMinicartItem(item, i + 1)
      this.minicartItemContainer.item.insertAdjacentHTML('beforeend', template)
      i++
    }
  }

  async buildItem(item, lineId) {
    const minicartItemElement = this.minicartItemContainer.item?.querySelector(
      `.mini-cart-item[data-line-id="${lineId}"]`,
    )
    const cartItemElement = this.cartTable.item?.querySelector(
      `.cart-item[data-line-id="${lineId}"]`,
    )

    if (minicartItemElement) {
      minicartItemElement.outerHTML = await templateMinicartItem(item, lineId)
    }

    if (cartItemElement) {
      cartItemElement.outerHTML = await templateCartItem(item, lineId)
    }
  }

  async buildDiscounts() {
    const customerDiscounts =
      await window.app.components.cartApi.getCustomerDiscounts()
    const discounts = await window.app.components.cartApi.getCartDiscounts()
    const containers = this.discountContainers.items || []

    this.discountForms.forEach(form =>
      form.setAttribute(
        'aria-hidden',
        Object.entries(customerDiscounts).length > 0 ? 'true' : 'false',
      ),
    )

    for (const container of containers) {
      container.innerHTML = ''

      const customerItems = Object.entries(customerDiscounts)
      for (const [key, value] of customerItems) {
        container.innerHTML += await templateCustomerDiscountItem({
          key,
          ...value,
        })
      }

      const items = Object.entries(discounts)
      for (const [key, value] of items) {
        container.innerHTML += await templateDiscountItem({ key, ...value })
      }
    }
  }

  async updateSubtotal() {
    this.subtotals.forEach(subtotal => {
      subtotal
        .closest('.mini-cart__subtotal, .cart-summary__subtotal')
        ?.setAttribute(
          'aria-hidden',
          (this.cart.original_total_price === this.cart.total_price).toString(),
        )
      subtotal.textContent = formatMoney(this.cart.original_total_price)
    })
  }

  async updateTotal() {
    this.totals.forEach(total => {
      total.textContent = formatMoney(this.cart.total_price)
    })
  }

  async updateCount() {
    this.counts.forEach(count => {
      count.textContent = this.cart.item_count === 0 ? '' : this.cart.item_count
      if (this.cart.item_count > 0) {
        count.setAttribute('aria-hidden', 'false')
      } else {
        count.setAttribute('aria-hidden', 'true')
      }
    })
  }

  @bind
  async clearGlobalErrors() {
    this.errorContainers.forEach(item => (item.innerHTML = ''))
  }

  @bind
  clearAllErrors() {
    this.clearGlobalErrors()
    this.minicartItemContainer.item
      ?.querySelectorAll('.cart__error')
      ?.forEach(element => element.remove())
    this.cartTable.item
      ?.querySelectorAll('.cart__error')
      ?.forEach(element => element.remove())
  }

  @bind
  async clearItemErrors(lineId) {
    const minicartItemElement = this.minicartItemContainer.item?.querySelector(
      `.mini-cart-item[data-line-id="${lineId}"]`,
    )
    const cartItemElement = this.cartTable.item?.querySelector(
      `.cart-item[data-line-id="${lineId}"]`,
    )

    if (minicartItemElement) {
      minicartItemElement.querySelectorAll('.cart__error').forEach(element => {
        element.remove()
      })
    }

    if (cartItemElement) {
      cartItemElement.querySelectorAll('.cart__error').forEach(element => {
        element.remove()
      })
    }
  }

  @bind
  async renderError(error) {
    let targets = this.errorContainers?.items || []
    if (error instanceof CartItemError) {
      // Restore correct item details before showing errors
      await this.buildItem(error.lineItem, error.lineItem.lineId)

      targets = [
        this.minicartItemContainer.item?.querySelector(
          `[data-line-id="${error.lineItem.lineId}"] .mini-cart-item__info`,
        ),
        this.cartTable.item?.querySelector(
          `[data-line-id="${error.lineItem.lineId}"] .cart-item-product__info`,
        ),
      ]
    }

    targets = [...targets].filter(target => !!target)

    if (targets.length === 0) {
      return
    }

    const errorElement = document.createElement('span')
    errorElement.classList.add('cart__error')
    errorElement.textContent = error.message

    // Clear existing errors
    targets.forEach(target => {
      target
        .querySelectorAll('.cart__error')
        ?.forEach(element => element.remove())
      target.appendChild(errorElement.cloneNode(true))
    })

    document.dispatchEvent(new CartProcessingEndEvent())
  }

  @bind
  handleRemoveButtonClick(event) {
    event.preventDefault()

    const target = event.currentTarget || event.target
    const lineId = target.getAttribute('data-line-id')

    if (!lineId) {
      return
    }

    window.app.components.cartApi.removeLineItem(lineId)
  }

  handleDiscountSubmit(event) {
    event.preventDefault()
    const form = event.currentTarget || event.target
    const discountCode = form.querySelector('[name="discount_code"]')?.value

    try {
      window.app.components.cartApi.applyDiscount(discountCode)
    } catch (error) {
      if (error instanceof InvalidDiscountCodeError) {
        this.renderDiscountError(error)
      }
    }
  }

  @bind
  handleDiscountRemove(event) {
    event.preventDefault()

    window.app.components.cartApi.removeDiscount()
  }

  @bind
  handleSellingPlanChange(event) {
    const target = event.currentTarget || event.target
    const item = target.closest('.mini-cart-item__info-plan-group')
    const lineId = target.getAttribute('data-line-id')

    const toggle = item.querySelector('[data-selling-plan-toggle]')
    const select = item.querySelector('[data-selling-plan-select]')

    let sellingPlanId = null
    if (toggle.checked) {
      sellingPlanId = select.value
    }

    window.app.components.cartApi.updateSellingPlan(lineId, sellingPlanId)
  }

  @bind
  updateDeliveryMessages() {
    const totalPrice = this.cart.total_price

    const freeShipping = parseInt(
      window.theme.free_shipping.free_shipping_amount,
    )
    const freeShippingMoney = window.theme.free_shipping.free_shipping_money

    if (this.deliveryNotificationMessages.length === 0) {
      return
    }

    let deliveryMessage = window.theme.free_shipping.empty_html.replace(
      '{{ money }}',
      freeShippingMoney,
    )

    if (totalPrice >= 1 && totalPrice < freeShipping) {
      const priceDiff = formatMoney(freeShipping - totalPrice)
      deliveryMessage = window.theme.free_shipping.between_html.replace(
        '{{ money }}',
        priceDiff,
      )
    } else if (totalPrice >= freeShipping) {
      deliveryMessage = window.theme.free_shipping.over
    }

    this.deliveryNotificationMessages.forEach(message => {
      message.innerHTML = deliveryMessage
    })

    let cartTotal = totalPrice * 100

    let percentage =
      cartTotal / freeShipping < 100 ? cartTotal / freeShipping : '100'

    document.querySelector(
      '.delivery-notification-progress__spent',
    ).style.width = percentage + '%'
  }
}
